// Cross-tab of one measure: a row per area (or group), a column per month /
// sex / age group / tribe, heat-shaded, with row and column totals.
// Only the first PAGE rows are drawn (30k polling centres would freeze the
// page); search and "Show more" reach the rest, and the CSV has every row.

import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, InputBase, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { C, fmt, Toggle } from './ui';

export interface PivotRow { key: string; label: string; values: number[] }

type Mode = 'count' | 'row' | 'col';

const PAGE = 200;

export const PivotTable: React.FC<{
  rowHeader: string;
  columns: string[];
  rows: PivotRow[];
  onRowClick?: (key: string) => void;
  fileName?: string;
  maxHeight?: number;
}> = ({ rowHeader, columns, rows, onRowClick, fileName = 'pivot', maxHeight = 460 }) => {
  const [mode, setMode] = useState<Mode>('count');
  const [sortCol, setSortCol] = useState<number>(-1); // -1 = total
  const [desc, setDesc] = useState(true);
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search.trim().toLowerCase());
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [rows, query, sortCol, desc]);

  const colTotals = useMemo(() => columns.map((_, j) => rows.reduce((s, r) => s + r.values[j], 0)), [rows, columns]);
  const grand = colTotals.reduce((a, b) => a + b, 0);

  const sorted = useMemo(() => {
    const list = rows.map((r) => ({ ...r, total: r.values.reduce((a, b) => a + b, 0) }));
    const v = (r: typeof list[number]) => (sortCol < 0 ? r.total : r.values[sortCol]);
    list.sort((a, b) => (desc ? v(b) - v(a) : v(a) - v(b)));
    return list;
  }, [rows, sortCol, desc]);

  const matching = useMemo(
    () => (query ? sorted.filter((r) => r.label.toLowerCase().includes(query)) : sorted),
    [sorted, query],
  );
  const shown = matching.length > limit ? matching.slice(0, limit) : matching;

  const max = useMemo(() => {
    let m = 0;
    for (const r of sorted) for (let j = 0; j < columns.length; j++) {
      const x = show(r.values[j], r.total, colTotals[j]);
      if (x > m) m = x;
    }
    return m || 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, mode, colTotals, columns.length]);

  function show(v: number, rowTotal: number, colTotal: number) {
    if (mode === 'row') return rowTotal ? (v / rowTotal) * 100 : 0;
    if (mode === 'col') return colTotal ? (v / colTotal) * 100 : 0;
    return v;
  }
  const label = (x: number) => (mode === 'count' ? fmt(x) : `${x.toFixed(1)}%`);

  const exportCsv = () => {
    const esc = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [
      [rowHeader, ...columns, 'Total'].map(esc).join(','),
      ...sorted.map((r) => [r.label, ...r.values.map((v, j) => show(v, r.total, colTotals[j]).toFixed(mode === 'count' ? 0 : 1)), r.total].map(esc).join(',')),
      ['Total', ...colTotals, grand].map(esc).join(','),
    ];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `${fileName}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const head = (text: string, j: number, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} onClick={() => { if (sortCol === j) setDesc(!desc); else { setSortCol(j); setDesc(true); } }}
      sx={{
        backgroundColor: C.cardAlt, color: sortCol === j ? C.text : C.muted, fontSize: '0.64rem', fontWeight: 700, letterSpacing: 0.3,
        borderBottom: `1px solid ${C.border}`, py: 0.75, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
      }}>
      {text}{sortCol === j ? (desc ? ' ▼' : ' ▲') : ''}
    </TableCell>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
        <Toggle value={mode} onChange={(v) => setMode(v as Mode)} options={[
          { value: 'count', label: 'Count' }, { value: 'row', label: '% of row' }, { value: 'col', label: '% of column' },
        ]} />
        <Box sx={{ flex: 1 }} />
        {sorted.length > PAGE && (
          <InputBase value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${rowHeader.toLowerCase()}…`}
            sx={{ fontSize: '0.75rem', color: C.text, px: 1, py: 0.25, border: `1px solid ${C.border}`, borderRadius: 1, width: 200 }} />
        )}
        <Typography sx={{ fontSize: '0.68rem', color: C.muted }}>
          {shown.length < matching.length ? `top ${fmt(shown.length)} of ${fmt(matching.length)}` : fmt(matching.length)} rows · click a header to sort
        </Typography>
        <Tooltip title="Download CSV"><IconButton size="small" onClick={exportCsv}><FileDownloadIcon sx={{ fontSize: 18, color: C.muted }} /></IconButton></Tooltip>
      </Box>
      <TableContainer sx={{ maxHeight, flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: C.cardAlt, color: C.muted, fontSize: '0.64rem', fontWeight: 700, borderBottom: `1px solid ${C.border}`, position: 'sticky', left: 0, zIndex: 3, minWidth: 160 }}>
                {rowHeader.toUpperCase()}
              </TableCell>
              {columns.map((c, j) => <React.Fragment key={c}>{head(c.toUpperCase(), j)}</React.Fragment>)}
              {head('TOTAL', -1)}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.key} hover onClick={onRowClick ? () => onRowClick(r.key) : undefined} sx={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                <TableCell sx={{ color: C.text, fontSize: '0.76rem', fontWeight: 600, borderBottom: `1px solid ${C.border}`, py: 0.5, position: 'sticky', left: 0, backgroundColor: C.card, zIndex: 1, whiteSpace: 'nowrap' }}>
                  {r.label}
                </TableCell>
                {r.values.map((v, j) => {
                  const x = show(v, r.total, colTotals[j]);
                  const a = Math.min(1, x / max);
                  return (
                    <TableCell key={j} align="right" sx={{
                      fontSize: '0.74rem', borderBottom: `1px solid ${C.border}`, py: 0.5, whiteSpace: 'nowrap',
                      color: a > 0.55 ? C.bg : C.text, fontWeight: a > 0.55 ? 700 : 500,
                      backgroundColor: `rgba(0, 208, 132, ${0.06 + a * 0.8})`,
                    }}>{label(x)}</TableCell>
                  );
                })}
                <TableCell align="right" sx={{ color: C.green, fontSize: '0.76rem', fontWeight: 700, borderBottom: `1px solid ${C.border}`, py: 0.5 }}>
                  {mode === 'col' ? `${(grand ? (r.total / grand) * 100 : 0).toFixed(1)}%` : mode === 'row' ? '100%' : fmt(r.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableBody sx={{ position: 'sticky', bottom: 0 }}>
            <TableRow>
              <TableCell sx={{ backgroundColor: C.cardAlt, color: C.text, fontSize: '0.76rem', fontWeight: 700, position: 'sticky', left: 0, zIndex: 3, borderTop: `1px solid ${C.border}` }}>TOTAL</TableCell>
              {colTotals.map((t, j) => (
                <TableCell key={j} align="right" sx={{ backgroundColor: C.cardAlt, color: C.text, fontSize: '0.74rem', fontWeight: 700, borderTop: `1px solid ${C.border}` }}>
                  {mode === 'row' ? `${(grand ? (t / grand) * 100 : 0).toFixed(1)}%` : mode === 'col' ? '100%' : fmt(t)}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ backgroundColor: C.cardAlt, color: C.green, fontSize: '0.76rem', fontWeight: 700, borderTop: `1px solid ${C.border}` }}>{fmt(grand)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {shown.length < matching.length && (
        <Button size="small" onClick={() => setLimit((n) => n + PAGE * 5)} sx={{ color: C.green, fontSize: '0.7rem', alignSelf: 'center', my: 0.5 }}>
          Show {fmt(Math.min(PAGE * 5, matching.length - shown.length))} more of {fmt(matching.length - shown.length)} · the CSV has every row
        </Button>
      )}
      {onRowClick && <Typography sx={{ fontSize: '0.65rem', color: C.muted, px: 1, py: 0.5 }}>Click a row to drill into it</Typography>}
    </Box>
  );
};
